import { useCallback, useEffect, useRef, useState } from "react";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { usePractice } from "../context/PracticeContext";
import { useNavigate } from "@/lib/router";
import { useBuildAgent } from "../hooks/useBuildAgent";
import { applyProposal } from "../api/buildAgent";
import { graphsApi } from "../api/graphs";
import { cn } from "../lib/utils";
import { Layers, Send, Check, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

// Default model — matches BuildAgentPanel's graph-mode default (Sonnet 4).
const DEFAULT_MODEL_ID = "us.anthropic.claude-sonnet-4-20250514-v1:0";

/** Derive a short (~6 word) title from the first message. */
function deriveTitle(message: string): string {
  const words = message
    .trim()
    .replace(/[\n\r]+/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 6);
  const title = words.join(" ").trim();
  return title.length > 0 ? title : "New Agent";
}

/** One-line op summary, e.g. "+3 nodes, +2 edges". */
function summaryLine(summary: import("../api/buildAgent").GraphDiffProposal["summary"]): string {
  const parts: string[] = [];
  if (summary.addNodes) parts.push(`+${summary.addNodes} node${summary.addNodes > 1 ? "s" : ""}`);
  if (summary.removeNodes) parts.push(`-${summary.removeNodes} node${summary.removeNodes > 1 ? "s" : ""}`);
  if (summary.updateNodes) parts.push(`~${summary.updateNodes} node${summary.updateNodes > 1 ? "s" : ""}`);
  if (summary.addEdges) parts.push(`+${summary.addEdges} edge${summary.addEdges > 1 ? "s" : ""}`);
  if (summary.removeEdges) parts.push(`-${summary.removeEdges} edge${summary.removeEdges > 1 ? "s" : ""}`);
  if (summary.updateEdges) parts.push(`~${summary.updateEdges} edge${summary.updateEdges > 1 ? "s" : ""}`);
  return parts.join(", ");
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function AgentArchitect() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const { selectedPracticeId } = usePractice();
  const navigate = useNavigate();
  const companyId = selectedPracticeId ?? "";

  const agent = useBuildAgent(companyId);

  const [input, setInput] = useState("");
  const [graphId, setGraphId] = useState<string | null>(null);
  const [creatingGraph, setCreatingGraph] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Agents", href: "/agents" },
      { label: "Agent Architect" },
    ]);
  }, [setBreadcrumbs]);

  // Auto-scroll on new messages.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [agent.messages]);

  const handleSend = useCallback(async () => {
    const message = input.trim();
    if (!message || agent.isStreaming || creatingGraph) return;
    if (!companyId) {
      setLocalError("Select a practice before building an agent.");
      return;
    }
    setLocalError(null);

    let activeGraphId = graphId;

    // First message → create the draft graph that this conversation builds.
    if (!activeGraphId) {
      setCreatingGraph(true);
      try {
        const graph = await graphsApi.create(companyId, {
          name: deriveTitle(message),
          description: message,
          graphDefinition: {
            nodes: [{ id: "start", type: "start", position: { x: 300, y: 30 }, data: {} }],
            edges: [],
            config: {},
          },
        });
        activeGraphId = graph.id;
        setGraphId(graph.id);
      } catch (err) {
        setLocalError(
          `Could not create the draft graph: ${(err as Error).message ?? "unknown error"}`,
        );
        setCreatingGraph(false);
        return;
      }
      setCreatingGraph(false);
    }

    setInput("");
    agent.sendMessage(message, activeGraphId, DEFAULT_MODEL_ID, "graph");
  }, [input, agent, creatingGraph, companyId, graphId]);

  const handleApply = useCallback(
    async (proposalId: string) => {
      if (!agent.conversationId) return;
      try {
        await applyProposal(companyId, agent.conversationId, proposalId);
        agent.markProposal(proposalId, "accepted");
      } catch (err) {
        setLocalError(`Failed to apply proposal: ${(err as Error).message ?? "unknown error"}`);
      }
    },
    [companyId, agent],
  );

  const handleDismiss = useCallback(
    (proposalId: string) => {
      agent.markProposal(proposalId, "rejected");
    },
    [agent],
  );

  const hasAppliedProposal = agent.messages.some((m) => m.proposalStatus === "accepted");
  const isBusy = agent.isStreaming || creatingGraph;

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] -mx-4 sm:-mx-6 border-t border-border">
      {/* Page header */}
      <div className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-border shrink-0">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-indigo-100 dark:bg-indigo-900/40">
          <Layers className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h2 className="text-sm font-semibold leading-tight">Agent Architect</h2>
          <p className="text-xs text-muted-foreground">
            Describe an agent and the Build Agent assembles a real draft graph
          </p>
        </div>
        <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400">
          Platform
        </span>
      </div>

      {/* Split view */}
      <div className="flex flex-1 min-h-0">
        {/* Left: Chat */}
        <div className="flex flex-col flex-1 min-w-0 border-r border-border">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
            {agent.messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-center gap-3 text-muted-foreground">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-900/20">
                  <Layers className="h-6 w-6 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Describe the agent you need
                  </p>
                  <p className="text-xs mt-1 max-w-sm leading-relaxed">
                    e.g. "A monitor for Stage 4 Prior Auth rejection rates that alerts
                    billing when rejections exceed 15%." I'll propose a real graph you
                    can apply and open in Agent Studio.
                  </p>
                </div>
              </div>
            )}

            {agent.messages.map((msg) => (
              <div
                key={msg.id}
                className={cn(
                  "flex flex-col gap-1.5",
                  msg.role === "user" ? "items-end" : "items-start",
                )}
              >
                <div
                  className={cn(
                    "max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap",
                    msg.role === "user"
                      ? "bg-foreground text-background"
                      : "bg-accent text-foreground",
                  )}
                >
                  {msg.content ||
                    (msg.role === "assistant" && agent.isStreaming ? (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <span className="animate-pulse">●</span>
                        <span className="animate-pulse" style={{ animationDelay: "0.2s" }}>
                          ●
                        </span>
                        <span className="animate-pulse" style={{ animationDelay: "0.4s" }}>
                          ●
                        </span>
                      </span>
                    ) : null)}
                </div>

                {/* Graph diff proposal */}
                {msg.proposal && msg.proposalStatus && (
                  <div className="w-full max-w-[85%] rounded-lg border border-border bg-card/60 overflow-hidden">
                    <div className="flex items-start justify-between gap-2 px-3 py-2 border-b border-border bg-muted/30">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold text-foreground">
                          {msg.proposal.description}
                        </p>
                        {summaryLine(msg.proposal.summary) && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {summaryLine(msg.proposal.summary)}
                          </p>
                        )}
                      </div>
                      {msg.proposalStatus === "accepted" && (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 shrink-0">
                          <Check className="h-3 w-3" /> Applied
                        </span>
                      )}
                      {msg.proposalStatus === "rejected" && (
                        <span className="flex items-center gap-1 text-[10px] text-muted-foreground shrink-0">
                          <X className="h-3 w-3" /> Dismissed
                        </span>
                      )}
                    </div>

                    {msg.proposalStatus === "pending" && (
                      <div className="flex items-center gap-2 px-3 py-2">
                        <Button
                          size="sm"
                          className="h-7 text-[11px] flex-1"
                          onClick={() => handleApply(msg.proposal!.proposalId)}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Apply
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-[11px]"
                          onClick={() => handleDismiss(msg.proposal!.proposalId)}
                        >
                          <X className="h-3 w-3 mr-1" />
                          Dismiss
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {(agent.error || localError) && (
              <div className="text-[12px] text-destructive bg-destructive/10 rounded px-3 py-2">
                {localError ?? agent.error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 sm:px-6 py-3 border-t border-border shrink-0">
            {!companyId && (
              <p className="mb-2 text-[11px] text-destructive">
                Select a practice before building an agent.
              </p>
            )}
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
                placeholder="Describe the agent you need... (e.g. 'A monitor for Stage 4 Prior Auth rejection rates')"
                rows={2}
                disabled={isBusy || !companyId}
                className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60"
              />
              <Button
                size="sm"
                onClick={() => void handleSend()}
                disabled={!input.trim() || isBusy || !companyId}
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">
              {creatingGraph
                ? "Creating draft graph…"
                : "Press Enter to send · Shift+Enter for new line"}
            </p>
          </div>
        </div>

        {/* Right: Build summary */}
        <div className="hidden md:flex flex-col w-2/5 min-w-0">
          <div className="px-4 py-2 border-b border-border shrink-0">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Build
            </p>
          </div>
          <div className="flex-1 min-h-0 overflow-auto p-4">
            {!graphId ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-900/20">
                  <Layers className="h-8 w-8 text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm font-medium">No draft yet</p>
                  <p className="text-xs mt-1 max-w-xs leading-relaxed">
                    Send your first message and a draft graph will be created here for
                    the Build Agent to assemble.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div
                  className={cn(
                    "rounded-lg border p-4",
                    hasAppliedProposal
                      ? "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20"
                      : "border-border bg-card/60",
                  )}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-indigo-100 dark:bg-indigo-900/40">
                      <Layers className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <p className="text-sm font-medium text-foreground">
                      {hasAppliedProposal
                        ? "Changes applied to the draft graph"
                        : "Draft graph being assembled"}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                    {hasAppliedProposal
                      ? "Your accepted changes are committed to the draft. Open it in Agent Studio to review, edit, and deploy."
                      : "As you chat, the Build Agent proposes graph changes. Apply a proposal to commit it to the draft, then open it in Agent Studio."}
                  </p>
                  <Button
                    size="sm"
                    className="mt-3 w-full"
                    variant={hasAppliedProposal ? "default" : "outline"}
                    onClick={() => navigate(`/studio/${graphId}`)}
                  >
                    Open in Agent Studio
                    <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
